import { z } from 'zod';
/**
 * Доступность неизвестна: за период не пришло ни одной проверки.
 *
 * Ключ словаря, а не подпись: строка попадает прямо в ячейку таблицы,
 * и собирает её браузер на своём языке (§11, фаза 10).
 */
export const NO_AVAILABILITY_DATA = 'overview.availability.noData';
import { agentSiteSchema, agentStatusSchema } from './agents.js';
import { dependencyStatusSchema } from './health.js';
import { monitorEventSchema } from './monitors.js';
/**
 * Обзор (ТЗ §11, фаза 9, задача 1).
 *
 * Два блока с разными правами: сеть видит тот, кому можно читать узлы,
 * платформу — тот, кому можно площадки. Поэтому и запросов два, а не один
 * с половиной пустых полей: «блок не показывается» и «блок пуст» — разные
 * утверждения, и второе означало бы, что сеть в порядке, хотя её просто
 * не показали.
 */
// — Блок «Сеть» ————————————————————————————————————————————
/**
 * Доступность за период.
 *
 * Считается по пятиминутному агрегату `monitor_samples_5m`: своего хранилища
 * метрик платформа не заводит, а `ok_count` и `total_count` там уже лежат.
 * `ratio` — `null`, когда за период не было ни одной проверки: ноль процентов
 * и «проверок не было» — разные вещи, и рисовать второе первым значит пугать
 * пустой площадкой.
 */
export const availabilitySchema = z.object({
    okCount: z.number().int().nonnegative(),
    totalCount: z.number().int().nonnegative(),
    ratio: z.number().min(0).max(1).nullable(),
});
/**
 * Сводка по узлам.
 *
 * Состояние узла собирается из состояний его мониторов по тому же правилу,
 * что на карте и в списке узлов (§11, фаза 4, задача 2): сошлись — их общее,
 * разошлись — `warn`. Третье место, считающее это правило, обязано считать
 * его так же, иначе обзор будет спорить с картой.
 */
export const nodeTallySchema = z.object({
    total: z.number().int().nonnegative(),
    up: z.number().int().nonnegative(),
    /** Все мониторы узла не проходят. */
    down: z.number().int().nonnegative(),
    /** Мониторы разошлись: часть проходит, часть нет. */
    warn: z.number().int().nonnegative(),
    /** Проверок ещё не было либо мониторов у узла нет вовсе. */
    unknown: z.number().int().nonnegative(),
    /** Из `unknown` — те, у кого не заведено ни одного монитора. */
    withoutMonitors: z.number().int().nonnegative(),
});
/** Узел, чаще других менявший состояние за сутки. */
export const flappingNodeSchema = z.object({
    nodeId: z.uuid(),
    nodeName: z.string(),
    siteId: z.uuid(),
    siteName: z.string(),
    /** Сколько раз узел уходил в `down` за сутки. */
    downCount: z.number().int().nonnegative(),
    /** Всего смен состояния за сутки — вместе с подъёмами. */
    transitionCount: z.number().int().nonnegative(),
});
/**
 * Событие в общей ленте.
 *
 * То же, что в ленте площадки, плюс сама площадка: обзор смотрит на всю
 * организацию, и «Шлюз ушёл в down» без указания, чей это шлюз, не говорит
 * ничего — одинаковые имена узлов на разных площадках обычное дело.
 */
export const overviewEventSchema = monitorEventSchema.extend({
    siteId: z.uuid(),
    siteName: z.string(),
});
export const networkOverviewSchema = z.object({
    nodes: nodeTallySchema,
    monitors: z.object({
        total: z.number().int().nonnegative(),
        enabled: z.number().int().nonnegative(),
        down: z.number().int().nonnegative(),
    }),
    availability: z.object({
        day: availabilitySchema,
        week: availabilitySchema,
        month: availabilitySchema,
    }),
    /** Не более пяти: список для беглого взгляда, а не отчёт. */
    flapping: z.array(flappingNodeSchema),
    /** Лента последних смен состояния по всем площадкам организации. */
    events: z.array(overviewEventSchema),
    /**
     * Момент, на который посчитан обзор. Нужен, чтобы отличить свежие данные
     * от подвисшего экрана: «доступность 100 %» получасовой давности выглядит
     * так же, как сегодняшняя.
     */
    computedAt: z.iso.datetime(),
});
// — Блок «Платформа» ————————————————————————————————————————
/**
 * Агент площадки на обзоре.
 *
 * `uptimeSec` берётся из heartbeat и живёт в памяти процесса: время работы
 * есть только у подключённого агента, а у отключившегося его нет ни в каком
 * виде — последнее известное значение через час после обрыва означало бы
 * не «работает час», а «врёт час».
 */
export const platformAgentSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    /** Обслуживаемые площадки (ADR 032); пустой список — агент ни к чему не привязан. */
    sites: z.array(agentSiteSchema),
    status: agentStatusSchema,
    version: z.string().nullable(),
    platform: z.string().nullable(),
    lastSeenAt: z.iso.datetime().nullable(),
    uptimeSec: z.number().nonnegative().nullable(),
});
/**
 * Последняя резервная копия (§11, фаза 9, задача 5).
 *
 * `null` в `latest` — копий нет; `configured: false` — каталог копий вовсе
 * не задан. Это разные беды: во втором случае резервное копирование не
 * настроено, а в первом настроено и не сработало.
 */
export const backupStateSchema = z.object({
    configured: z.boolean(),
    latest: z
        .object({
        at: z.iso.datetime(),
        sizeBytes: z.number().int().nonnegative(),
        name: z.string(),
        /** Возраст копии в часах — по нему интерфейс решает, тревожиться ли. */
        ageHours: z.number().nonnegative(),
    })
        .nullable(),
    /** Почему состояние неизвестно: каталог не задан, не читается, пуст. */
    problem: z.string().nullable(),
});
/**
 * Что занимает место в базе.
 *
 * Показывается на обзоре потому, что это единственная величина платформы,
 * которая растёт сама и о которой узнают, когда кончается диск. Телеметрия
 * выделена отдельно не для красоты: у неё есть политики хранения (14 суток
 * сырых точек, 400 агрегата), и «база выросла вдвое» означает разное
 * в зависимости от того, выросла телеметрия или всё остальное.
 */
export const databaseSizeSchema = z.object({
    /** Вся база целиком, включая индексы и служебные таблицы TimescaleDB. */
    totalBytes: z.number().int().nonnegative(),
    /** Гипертаблица сырых проверок и непрерывный агрегат вместе. */
    telemetryBytes: z.number().int().nonnegative(),
    /**
     * Что занимает больше всего — не более пяти строк.
     *
     * Гипертаблицы считаются целиком, вместе с кусками: их куски лежат
     * в служебной схеме, и `pg_total_relation_size` по родительской таблице
     * показал бы у самой большой таблицы платформы ноль.
     */
    largest: z
        .array(z.object({
        name: z.string(),
        sizeBytes: z.number().int().nonnegative(),
    }))
        .max(5),
    /** Размер не прочитался: прав не хватило или запрос отказал. */
    problem: z.string().nullable(),
});
export const platformOverviewSchema = z.object({
    agents: z.array(platformAgentSchema),
    dependencies: z.object({
        database: dependencyStatusSchema,
        redis: dependencyStatusSchema,
        go2rtc: dependencyStatusSchema,
    }),
    /** Что открыто прямо сейчас: сессии VNC, живые потоки и записи архива. */
    sessions: z.object({
        vnc: z.number().int().nonnegative(),
        videoLive: z.number().int().nonnegative(),
        videoArchive: z.number().int().nonnegative(),
    }),
    backup: backupStateSchema,
    database: databaseSizeSchema,
    api: z.object({
        version: z.string(),
        uptimeSec: z.number().nonnegative(),
    }),
    computedAt: z.iso.datetime(),
});
// — Расчёт ————————————————————————————————————————————————
/** Доля успешных проверок; `null`, когда проверок не было. */
export function availabilityOf(okCount, totalCount) {
    return {
        okCount,
        totalCount,
        ratio: totalCount > 0 ? okCount / totalCount : null,
    };
}
/**
 * Доступность в процентах для показа.
 *
 * Два знака после запятой: разница между 99,95 % и 100 % — это четыре часа
 * простоя в год, и округлять её до «100 %» значит скрывать ровно то, ради
 * чего доступность и считают. Округление вниз по той же причине: 99,999 %
 * не должно показываться как ровные 100 %, пока простой был.
 */
export function formatAvailability(availability) {
    if (availability.ratio === null) {
        return NO_AVAILABILITY_DATA;
    }
    const percent = Math.floor(availability.ratio * 10_000) / 100;
    return `${percent.toFixed(2).replace('.', ',')} %`;
}
//# sourceMappingURL=overview.js.map